import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import LoadMoreButton from '@app/components/UI/LoadMoreButton/LoadMoreButton';
import MastheadNaked from '@app/components/UI/MastHead/MastheadNaked';
import MobileMenuSlideInMenu from '@app/components/UI/MobileSlideInMenu/MobileSlideInMenu';
import ModalVideo from '@app/components/UI/ModalVideo/ModalVideo';
import RichText from '@app/components/UI/RichText/RichText';
import SidebarWithCaptions from '@app/components/UI/SidebarWithCaptions/SidebarWithCaptions';
import { useIsMobile } from '@app/hooks/useIsMobile';
import imagePlaceholder from '@app/images/image-placeholder.png';
import { ContentEntity, ContentTopic, ContentType } from '@app/objects/ContentTopic';
import CustomRequestProvider from '@app/providers/CustomRequestProvider/CustomRequestProvider';
import HelmetProvider from '@app/providers/HelmetProvider/HelmetProvider';
import '@app/scss/components/blog.scss';

const ICON_LINK = [
	require('@app/images/arrow_circle.svg'),
	require('@app/images/text-button_link.svg'),
	require('@app/images/arrow_circle.svg'),
];

const TEXT_LINK = ['Read more', 'Visit the site', 'Watch Now'];

const initContentCount = 10;

const content = (
	<>
		<p>
			Lap of Love is dedicated to sharing its end-of-life pet care expertise to support veterinary teams and elevate compassionate standards
			across the profession. Through live and on-demand webinars and speaking engagements at conferences, we deliver practical insights on
			topics such as advanced euthanasia techniques and empathetic client communication, led by industry leaders like Dr. Mary Gardner,
			Co-Founder & CEO of Lap of Love; Dr. Sheilah Robertson, Lap of Love Medical Consultant; and Dr. Kathleen Cooney, Senior Director of
			Education for CAETA Educational Division of Lap of Love.
		</p>
		<p>
			Interested in hosting a Lap of Love speaker? Veterinary schools and event organizers can submit an interest form at the bottom of our
			events page.
		</p>
		<a
			href="https://info.lapoflove.com/events"
			target="_blank"
			rel="noopener noreferrer"
			className="btn btn-primary"
		>
			View Upcoming Events
		</a>
	</>
);

interface NewsContentProps {
	currentTopics: ContentTopic | undefined;
	searchQuery: string | undefined;
}

const NewsContent: React.FC<NewsContentProps> = ({ currentTopics, searchQuery }) => {
	const [modalVisible, setModalVisible] = React.useState<boolean>(false);
	const [videoSrc, setVideoSrc] = React.useState<string>('');
	const isMobile = useIsMobile();

	const context = useItemsProviderContext<ContentEntity>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { loading, items },
		actions: { handleChange },
	} = context;

	React.useEffect(() => {
		if (currentTopics) {
			handleChange({
				text: searchQuery || '',
				offset: 0,
			});
		}
	}, [currentTopics, searchQuery]);

	return (
		<>
			<div className="blog-page__content">
				{currentTopics && currentTopics.name === 'Speaking Engagements' && (
					<div className="blog-page__content--empty-text">
						<RichText text={content} />
					</div>
				)}
				{currentTopics && (
					<Helmet>
						<title>{`${currentTopics.name} | In The News | Lap of Love`}</title>
						<meta property="og:title" content={`${currentTopics.name} | In The News | Lap of Love`} />
						<meta name="twitter:title" content={`${currentTopics.name} | In The News | Lap of Love`} />
						<meta name="description" content={currentTopics.description || undefined} />
						<meta property="og:description" content={currentTopics.description || undefined} />
						<meta name="twitter:description" content={currentTopics.description || undefined} />
					</Helmet>
				)}

				{!loading && items.map((item, index) => (
					<article key={index} className="article">
						<div className="article__info">
							{isMobile && (
								<p className="article__name-date mobile">
									{item.source && <>
										{item.source}
										,&nbsp;
									</>}
									{new Intl.DateTimeFormat('en-US', {
										year: 'numeric',
										month: 'long',
										day: '2-digit',
										timeZone: 'UTC',
									}).format(item.date)}
								</p>
							)}
							<h6 className="article__caption">{item.title}</h6>
							<p className="article__text">{item.smallText}</p>
							<div className="article__addition">
								{item.contentType === ContentType.Video ? (
									<span
										onClick={() => {
											setModalVisible(true);
											setVideoSrc(item.url);
										}}
										className="article__more btn-text"
									>
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</span>
								) : item.contentType === ContentType.Article ? (
									<Link to={item.url} className="article__more btn-text" target="self">
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</Link>
								) : (
									<a href={item.url} className="article__more btn-text" target="_blank" rel="noopener noreferrer">
										{TEXT_LINK[item.contentType]}
										{' '}
										<img className="arrow-icon" src={ICON_LINK[item.contentType]} alt="Arrow" />
									</a>
								)}

								{!isMobile && (
									<span className="article__name-date">
										{item.source && (
											<>
												{item.source}
												,&nbsp;
											</>
										)}
										{item.date > 0 && (
											new Intl.DateTimeFormat('en-US', {
												year: 'numeric',
												month: 'long',
												day: '2-digit',
												timeZone: 'UTC',
											}).format(item.date)
										)}
									</span>
								)}
							</div>
						</div>
						<div className="article__photo">
							<CustomAvatar src={item.avatar} errorAvatar={imagePlaceholder} maxWidth={300} maxHeight={300} />
						</div>
					</article>
				))}

				{!loading && !items.length && currentTopics?.url !== 'speaking-engagements' && (
					<div className="faq-block__dummy">No issues found</div>
				)}

				{loading && <BounceLoader />}
				<div className="text-center">
					<LoadMoreButton
						className="btn btn-lg btn-secondary blog-page__load-more"
						visible={!loading}
					/>
				</div>
			</div>

			<ModalVideo modalVisible={modalVisible} setModalVisible={setModalVisible} videoSrc={videoSrc} />
		</>
	);
};

const NewsTopics: React.FC = () => {
	const context = useItemsProviderContext<ContentTopic>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items },
		actions: { handleChange },
	} = context;

	const [currentTopics, setCurrentTopics] = React.useState<ContentTopic>();

	const [navCollapse, setNavCollapse] = React.useState(false);

	const [searchFieldValue, setSearchFieldValue] = React.useState('');
	const [searchQuery, setSearchQuery] = React.useState('');

	const params = useParams<{ id: string }>();

	const onSearchSubmit = (value) => {
		setSearchQuery(value);
		setSearchFieldValue(value);
	};

	React.useEffect(() => {
		handleChange();
	}, []);

	React.useEffect(() => {
		if (items.length) {
			if (params.id && items.length) {
				items.forEach((item) => {
					if (item.url === params.id) {
						setCurrentTopics(item);
						return false;
					}
				});
			}
		}
	}, [items]);

	React.useEffect(() => {
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	}, []);

	return (
		<div className="blog-page">
			<MastheadNaked imageSrc={require('@app/images/blog-bg.jpg')} title={`${currentTopics ? currentTopics.name : 'In the News'}`} />
			<div className="blog-page-body">
				<MobileMenuSlideInMenu
					topics={items}
					onSetIssue={setCurrentTopics}
					newsPage
					onSubmit={onSearchSubmit}
					searchFieldValue={searchFieldValue}
					setSearchFieldValue={setSearchFieldValue}
				/>
				<div className="blog-page__sidebar">
					<SidebarWithCaptions
						onSetIssue={setCurrentTopics}
						topics={items}
						searchFieldPlaceHolder="Search by topic"
						typeTopicsInner="News"
						newsPage
						searchFieldValue={searchFieldValue}
						setSearchFieldValue={setSearchFieldValue}
						onSubmit={onSearchSubmit}
					/>
				</div>

				<ItemsProvider<ContentEntity>
					type={`contentTopics/news/${params.id ? `${params.id}/` : ''}content`}
					loadRequest={`contentTopics/news/${params.id ? `${params.id}/` : ''}content`}
					pagination={{ pageSize: initContentCount, total: 0 }}
				>
					<NewsContent currentTopics={currentTopics} searchQuery={searchQuery} />
				</ItemsProvider>
			</div>
		</div>
	);
};

const News: React.FC = () => {
	return (
		<CustomRequestProvider version="v2">
			<HelmetProvider ignoreServerPage>
				<ItemsProvider<ContentTopic>
					type="contentTopics/news"
					loadRequest="contentTopics/news"
				>
					<NewsTopics />
				</ItemsProvider>
			</HelmetProvider>
		</CustomRequestProvider>
	);
};

export default News;
